import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import swal from 'sweetalert';

import {
  useGetSystemRoutesQuery,
} from '../../Features/RoutesSlice';

import {
  useCheckClientIsSubscribedToRouteMutation,
  useSubscribeToRoutesMutation,
} from '../../Features/clientsRoutesSlice';

import { selectCurrentUser } from '../../Features/auth/authSlice';
import { useSelector } from 'react-redux';

import LoadingBar from 'react-top-loading-bar';
import { useIsOnline } from 'react-use-is-online';
import { useGetClientByIdQuery } from '../../Features/clientsSlice';

import ErrorView from '../ErrorView';

import {
  ChevronLeftIcon,
  CheckCircleIcon,
  LocationMarkerIcon,
  SaveIcon,
} from '@heroicons/react/outline';

function ManageSystemRoutesForClientSubscriptionView() {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const { isOnline } = useIsOnline();

  const user = useSelector(selectCurrentUser);

  const [input, setInput] = useState({
    client_routes: [],
  });

  const {
    data: client,
    isSuccess: isClientSuccessful,
  } = useGetClientByIdQuery(user.id);

  const {
    data: routes,
    isLoading,
    isSuccess,
    isError,
  } = useGetSystemRoutesQuery();

  useEffect(() => {
    setProgress(30);

    if (isLoading) {
      setProgress(80);
    }

    if (isSuccess) {
      setProgress(100);
    }
  }, [isLoading, isSuccess]);

  const [
    checkClientIsSubscribedToRoute,
    { isLoading: isLoadingRouteClientStatus },
  ] = useCheckClientIsSubscribedToRouteMutation();

  const handleChange = async (e) => {
    const { value, checked } = e.target;
    const { client_routes } = input;

    if (checked) {
      try {
        setProgress(80);

        await checkClientIsSubscribedToRoute({
          route_id: value,
          user_id: user.id,
        }).unwrap();

        setInput({
          client_routes: [...client_routes, value],
        });

        setProgress(100);
      } catch (err) {
        setProgress(100);

        swal(
          'Action failed',
          err?.data?.message || 'Unable to select this route.',
          'error'
        );
      }
    } else {
      setInput({
        client_routes: client_routes.filter((e) => e !== value),
      });
    }
  };

  const [
    subscribeToRoutes,
    { isLoading: isLoadingSubscribe },
  ] = useSubscribeToRoutesMutation();

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const reqBoby = {
        user_id: user.id,
        routes: input.client_routes,
      };

      setProgress(80);

      const resp = await subscribeToRoutes(reqBoby).unwrap();

      if (resp) {
        setProgress(100);
        navigate(-1);
      }
    } catch (err) {
      setProgress(100);

      if (err?.data?.success === false) {
        swal(err.data.message);
      } else {
        swal(
          'Error',
          'Something went wrong while saving route subscriptions.',
          'error'
        );
      }
    }
  };

  const back = () => {
    if (!isOnline) {
      return swal(
        'No Internet connection',
        'Please check your Internet connection and try again',
        'error'
      );
    }

    setProgress(100);
    navigate(-1);
  };

  const selectedCount = input.client_routes.length;

  return (
    <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">
      <LoadingBar
        color="#80b643"
        height="4px"
        progress={progress}
        onLoaderFinished={() => setProgress(0)}
      />

      {/* Header */}
      <div className="w-full px-4 sm:px-6 lg:px-8 pt-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

            <div className="flex items-center gap-3 min-w-0">
              <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                <LocationMarkerIcon className="w-6 h-6 text-[#80b643]" />
              </div>

              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                  Route Subscriptions
                </h1>

                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Select the routes this client is subscribed to
                </p>
              </div>
            </div>

            {isSuccess && (
              <button
                type="button"
                onClick={back}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
              >
                <ChevronLeftIcon className="w-4 h-4" />
                Back
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 sm:px-6 lg:px-8 mt-6">
        <div className="max-w-4xl mx-auto">

          {isLoading ? (
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm">
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-10 h-10 border-4 border-gray-200 dark:border-gray-700 border-t-[#80b643] rounded-full animate-spin" />

                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                  Loading routes...
                </p>
              </div>
            </div>
          ) : isError ? (
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-6">
              <ErrorView />
            </div>
          ) : isSuccess ? (
            <form onSubmit={handleSubmit}>
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

                {/* Card Header */}
                <div className="p-5 sm:p-6 border-b border-gray-200 dark:border-gray-800">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                        <LocationMarkerIcon className="w-5 h-5 text-[#80b643]" />
                      </div>

                      <div>
                        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                          Available Routes
                        </h2>

                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Choose the routes available to this client
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-[#80b643]/10 text-[#80b643] text-xs font-medium">
                        <CheckCircleIcon className="w-4 h-4" />
                        {selectedCount} selected
                      </span>
                    </div>
                  </div>
                </div>

                {/* Routes */}
                <div>
                  {routes?.data?.length > 0 ? (
                    <div className="divide-y divide-gray-200 dark:divide-gray-800">

                      {routes.data.map((data) => {
                        const routeId = String(data.id);

                        const isSelected =
                          input.client_routes.includes(routeId);

                        return (
                          <label
                            key={data.id}
                            className={`flex items-center justify-between gap-4 px-5 sm:px-6 py-4 cursor-pointer transition ${
                              isSelected
                                ? 'bg-[#80b643]/5 dark:bg-[#80b643]/10'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                            }`}
                          >
                            <div className="flex items-center gap-3 min-w-0">

                              <div
                                className={`w-9 h-9 flex-shrink-0 rounded-lg flex items-center justify-center ${
                                  isSelected
                                    ? 'bg-[#80b643]/10 dark:bg-[#80b643]/20'
                                    : 'bg-gray-100 dark:bg-gray-800'
                                }`}
                              >
                                <LocationMarkerIcon
                                  className={`w-5 h-5 ${
                                    isSelected
                                      ? 'text-[#80b643]'
                                      : 'text-gray-400 dark:text-gray-500'
                                  }`}
                                />
                              </div>

                              <div className="min-w-0">
                                <p
                                  className={`text-sm font-medium truncate ${
                                    isSelected
                                      ? 'text-[#80b643]'
                                      : 'text-gray-700 dark:text-gray-200'
                                  }`}
                                >
                                  {data.start_off_station_name}
                                  {' - '}
                                  {data.destination_station_name}
                                </p>

                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                  Route
                                </p>
                              </div>
                            </div>

                            <input
                              type="checkbox"
                              name="roles[]"
                              value={data.id}
                              checked={isSelected}
                              onChange={handleChange}
                              disabled={isLoadingRouteClientStatus}
                              className="w-5 h-5 flex-shrink-0 rounded border-gray-300 text-[#80b643] focus:ring-[#80b643] cursor-pointer disabled:opacity-50"
                            />
                          </label>
                        );
                      })}

                    </div>
                  ) : (
                    <div className="py-14 px-6 text-center">
                      <div className="w-14 h-14 mx-auto rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                        <LocationMarkerIcon className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                      </div>

                      <h3 className="mt-4 text-sm font-semibold text-gray-700 dark:text-gray-200">
                        No routes available
                      </h3>

                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        There are currently no system routes available for subscription.
                      </p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="px-5 sm:px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/30">
                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">

                    <button
                      type="button"
                      onClick={back}
                      disabled={isLoadingSubscribe}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-medium text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                      Cancel
                    </button>

                    <button
                      type="submit"
                      disabled={isLoadingSubscribe || isLoadingRouteClientStatus}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#80b643] hover:bg-[#719f3a] text-white font-medium text-sm transition disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isLoadingSubscribe ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <SaveIcon className="w-4 h-4" />
                          Save Routes
                        </>
                      )}
                    </button>

                  </div>
                </div>

              </div>
            </form>
          ) : null}

        </div>
      </div>
    </div>
  );
}

export default ManageSystemRoutesForClientSubscriptionView;